import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema, PaginatedResponseSchema } from './common';

/**
 * Unidad curricular = materia, módulo o unidad didáctica que forma parte
 * de un programa académico. Una unidad curricular es una **plantilla** del
 * programa; cuando se crea una cohorte, las sesiones concretas pueden
 * referenciar estas unidades para indicar qué módulo se imparte.
 *
 * MVP: solo `nombre` es obligatorio. El resto de los campos académicos
 * (horas, créditos) son opcionales para no bloquear la carga inicial.
 */
export const UnidadCurricularSchema = z.object({
  id: EntityIdSchema,
  programa_id: EntityIdSchema,
  orden: z.number().int().nonnegative(),
  nombre: z.string().min(1),
  horas_modalidad_a: z.number().int().nonnegative().nullable().optional(),
  horas_modalidad_b: z.number().int().nonnegative().nullable().optional(),
  total_horas: z.number().int().nonnegative().nullable().optional(),
  creditos: z.number().int().nonnegative().nullable().optional(),
  created_at: IsoDateTimeSchema,
});
export type UnidadCurricular = z.infer<typeof UnidadCurricularSchema>;

/** Input para POST /api/v1/programas/:id/unidades-curriculares. */
export const UnidadCurricularCreateInputSchema = z.object({
  orden: z.number().int().nonnegative().default(0),
  nombre: z.string().min(1).max(200),
  horas_modalidad_a: z.number().int().nonnegative().optional(),
  horas_modalidad_b: z.number().int().nonnegative().optional(),
  creditos: z.number().int().nonnegative().optional(),
});
export type UnidadCurricularCreateInput = z.infer<typeof UnidadCurricularCreateInputSchema>;

/** Input para PATCH /api/v1/unidades-curriculares/:id (todos opcionales). */
export const UnidadCurricularUpdateInputSchema = z.object({
  orden: z.number().int().nonnegative().optional(),
  nombre: z.string().min(1).max(200).optional(),
  horas_modalidad_a: z.number().int().nonnegative().optional(),
  horas_modalidad_b: z.number().int().nonnegative().optional(),
  creditos: z.number().int().nonnegative().optional(),
});
export type UnidadCurricularUpdateInput = z.infer<typeof UnidadCurricularUpdateInputSchema>;

/** Response de GET /api/v1/programas/:id/unidades-curriculares. */
export const UnidadCurricularListResponseSchema = PaginatedResponseSchema(UnidadCurricularSchema);
export type UnidadCurricularListResponse = z.infer<typeof UnidadCurricularListResponseSchema>;
